import React, { useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { apiClient } from "@api/client";
import type {
  ApprovalRequestResponse,
  ApprovalHistoryEvent,
  ApprovalDecisionRequest,
  ApprovalDecisionResponse,
  PendingApprovalsResponse,
} from "@schemas/api";
import { useNotificationStore } from "@store/notificationStore";
import { useAuthStore } from "@store/authStore";
import type { OpenApprovalList, OpenApprovalRequest } from "@schemas/api";

const OPEN_APPROVALS_REFRESH_INTERVAL_MS = 10_000;

export const useOpenApprovals = (
  skip = 0,
  limit = 25,
  recordType?: OpenApprovalRequest["record_type"],
) => {
  const tenant = useAuthStore(
    (s) => s.activeTenantId ?? s.currentUser?.active_tenant_id,
  );
  const user = useAuthStore((s) => s.currentUser?.id);
  return useQuery({
    queryKey: [
      "open-approvals",
      tenant,
      user,
      recordType ?? "all",
      skip,
      limit,
    ],
    enabled: !!tenant && !!user,
    queryFn: async () =>
      (
        await apiClient.get<OpenApprovalList>("/approvals/open", {
          params: { skip, limit, record_type: recordType },
        })
      ).data,
    // Every filter tab uses this hook. Do not let the app-wide five-minute
    // cache or background-tab defaults leave a queue stale after tab changes.
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
    refetchInterval: OPEN_APPROVALS_REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: true,
  });
};

/**
 * Fetch pending approvals for the current user
 * Used to show "My Approvals" list
 */
export const usePendingApprovals = (skip = 0, limit = 20) => {
  return useQuery({
    queryKey: ["pending-approvals", skip, limit],
    queryFn: async () => {
      const { data } = await apiClient.get<PendingApprovalsResponse>(
        "/approvals/me/pending-approvals",
        {
          params: { skip, limit },
        },
      );
      return {
        items: data.items ?? data.pending_approvals ?? [],
        total: data.total ?? data.count ?? 0,
        skip,
        limit,
      };
    },
    refetchInterval: 10000, // Refetch every 10 seconds for live updates
  });
};

/**
 * Get details of a specific approval request
 * Shows current step, approvers, history, etc.
 */
export const useApprovalRequestDetails = (requestId: string | null) => {
  return useQuery({
    queryKey: ["approval-request", requestId],
    queryFn: async () => {
      if (!requestId) return null;
      const { data } = await apiClient.get<ApprovalRequestResponse>(
        `/approvals/${requestId}`,
      );
      return data;
    },
    enabled: !!requestId,
    refetchInterval: 5000, // Poll for updates every 5 seconds
  });
};

/**
 * Get approval step responses (history of who approved/rejected at each step)
 * Shows audit trail of the approval chain
 */
export const useApprovalStepResponses = (requestId: string | null) => {
  return useQuery({
    queryKey: ["approval-step-responses", requestId],
    queryFn: async () => {
      if (!requestId) return [];
      const { data } = await apiClient.get<ApprovalHistoryEvent[]>(
        `/approvals/${requestId}/history`,
      );
      return data;
    },
    enabled: !!requestId,
    refetchInterval: 3000,
  });
};

/**
 * Submit an approval or rejection decision
 * Called when user clicks Approve/Reject button in ApprovalChainReviewUI
 */
export const useSubmitApprovalRequest = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async ({
      requestId,
      decision,
      comments,
    }: {
      requestId: string;
      decision: "approved" | "rejected";
      comments?: string;
    }) => {
      const { data } = await apiClient.post<ApprovalDecisionResponse>(
        `/approvals/${requestId}/approve`,
        {
          decision,
          comments,
        } as ApprovalDecisionRequest,
      );
      return data;
    },
    onSuccess: (data, variables) => {
      addNotification({
        message: `Approval request ${variables.decision} successfully`,
        type: "success",
      });

      // Invalidate related queries to refresh lists
      queryClient.invalidateQueries({ queryKey: ["pending-approvals"] });
      queryClient.invalidateQueries({ queryKey: ["open-approvals"] });
      queryClient.invalidateQueries({
        queryKey: ["approval-request", variables.requestId],
      });
      queryClient.invalidateQueries({
        queryKey: ["approval-step-responses", variables.requestId],
      });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message:
          error.response?.data?.detail || "Failed to submit approval decision",
        type: "error",
      });
    },
  });
};

/**
 * Poll for approval request status
 * Used to detect when an approval chain completes
 * Useful for showing "Approved" notification or closing modal
 */
export const useApprovalRequestStatus = (
  requestId: string | null,
  onStatusChange?: (status: ApprovalRequestResponse["status"]) => void,
) => {
  const previousStatusRef = useRef<string | null>(null);

  const query = useQuery({
    queryKey: ["approval-request-status", requestId],
    queryFn: async () => {
      if (!requestId) return null;
      const { data } = await apiClient.get<ApprovalRequestResponse>(
        `/approvals/${requestId}`,
      );
      return data;
    },
    enabled: !!requestId,
    refetchInterval: 3000, // Poll every 3 seconds
  });

  // Call callback when status changes
  useEffect(() => {
    if (
      query.data &&
      previousStatusRef.current !== query.data.status &&
      onStatusChange
    ) {
      previousStatusRef.current = query.data.status;
      onStatusChange(query.data.status);
    }
  }, [query.data, onStatusChange]);

  return query;
};
